/**
 * Design tab field controls.
 *
 * Visual parity with the Scene Editor / PlayEngine inspectors — same FieldRow
 * geometry, same dark input chrome — but SELF-CONTAINED: this imports nothing
 * from either of those panels and neither of them imports this. The Scene
 * Editor made the same call for the same reason ("copied for scene editor only;
 * does not import or modify PlayEngine code"), and the reason holds here too:
 * three product modes share this screen, and a widget shared between them is a
 * widget that cannot be changed for one without testing all three.
 *
 * What is deliberately NOT copied is the multi-select machinery — `mixed`
 * values, `targetIds`, gesture deltas applied across components. The Design tab
 * edits one snapshot's globalVariables, so a field has exactly one value.
 */

import React, { useEffect, useRef, useState } from "react";
import { useEditableValue } from "../../customHooks/useEditableValue";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import PublishIcon from "@material-ui/icons/Publish";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import EditIcon from "@material-ui/icons/Edit";
import DialogTextEdit from "../dialogTextEdit";
import { describeAssetSize } from "./designAssetSource";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const PANEL_BG = "#2b2b2e";
export const SECTION_DIVIDER = "#3a3a3f";
export const FIELD_LABEL = "#b8bcc4";
export const BAR_BG = "#3a3a41";
/** Menus and dialogs float above the panel and take the lighter surface. */
export const CARD_BG = "#474747";

/** Live preview and persistence are both debounced by this, together. */
export const DESIGN_SYNC_DEBOUNCE_MS = 200;

const SCRUB_STEP = 0.01;
const SCRUB_STEP_SHIFT = 0.1;
const ARROW_STEP = 0.01;
const ARROW_STEP_SHIFT = 0.05;
const ARROW_REPEAT_DELAY_MS = 400;
const ARROW_REPEAT_INTERVAL_MS = 60;
/** Horizontal pixels per step while dragging a number field. */
const STEP_PIXEL = 14;

/**
 * The preview iframe a scrub must not drop the pointer into.
 *
 * Which iframe that is depends on the host — the version editor and the
 * snapshot editor each render their own — so the panel tells this module once,
 * on mount, rather than every field carrying the id down through FieldRow.
 */
let previewIframeId = "gamePreviewIframe";

export function setDesignPreviewIframeId(id) {
  if (typeof id === "string" && id) previewIframeId = id;
}

const SCRUB_CURSOR_ID = "design-scrub-cursor";

function roundToStep(num, step) {
  const inv = 1 / step;
  return Math.round(num * inv) / inv;
}

/** Drop the tail binary floats grow when drag offsets are added up. */
function roundFloatNoise(num) {
  return Math.round(num * 1e4) / 1e4;
}

/**
 * One labelled row: label on the left at a fixed share of the width, control
 * filling the rest, optional action pinned right.
 */
export function FieldRow({ label, tooltip, readOnly = false, action = null, children }) {
  const labelText = typeof label === "string" ? label : undefined;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: "30px",
        padding: "4px 6px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div
        title={labelText}
        style={{
          display: "flex",
          alignItems: "center",
          flex: "0 0 20%",
          maxWidth: "20%",
          minWidth: 0,
          minHeight: 24,
          fontSize: "12px",
          color: readOnly ? "#6f7278" : FIELD_LABEL,
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            lineHeight: 1.35,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {label}
        </span>
        {tooltip ? (
          <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>} arrow placement="right">
            <span style={{ display: "inline-flex", flexShrink: 0, marginLeft: 4, cursor: "help" }}>
              <HelpOutlineIcon style={{ fontSize: 13, color: "#d0d3d8" }} />
            </span>
          </Tooltip>
        ) : null}
      </div>

      <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
        <div style={{ width: "100%", minWidth: 0 }}>{children}</div>
      </div>

      {action ? (
        <div style={{ display: "flex", alignItems: "center", minHeight: 24, flexShrink: 0 }}>{action}</div>
      ) : null}
    </div>
  );
}

export function SectionHeader({ title, right = null }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 6px 6px" }}>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      <span
        style={{
          fontSize: "11px",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "#8a8d93",
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {title}
      </span>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      {right}
    </div>
  );
}

/**
 * Which orientation the row below is editing.
 *
 * A responsive value holds a portrait and a landscape number, and every other
 * panel in this product shows ONE of them — the one the preview is currently
 * in — with this badge above the control. Same 9px uppercase tag as the Scene
 * Editor and the playEngine version settings, so the three read as one editor.
 */
export function OrientationTag({ isPortrait }) {
  return (
    <div
      style={{
        fontSize: "9px",
        color: "#8a8d93",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        marginBottom: "2px",
      }}
    >
      {isPortrait ? "Portrait" : "Landscape"}
    </div>
  );
}

export function BooleanField({ value, disabled, onChange, onCommit = () => {} }) {
  const [localValue, setLocalValue] = useState(Boolean(value));

  useEffect(() => {
    setLocalValue(Boolean(value));
  }, [value]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: 24,
        width: "100%",
      }}
    >
      <Switch
        size="small"
        checked={localValue}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.checked;
          setLocalValue(next);
          onChange(next);
          onCommit();
        }}
        color="primary"
        style={{ margin: 0 }}
      />
    </div>
  );
}

export function StringField({ value, disabled, onChange, onCommit = () => {} }) {
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(String(value ?? ""));
  }, [value]);

  return (
    <TextField
      multiline
      minRows={1}
      maxRows={8}
      size="small"
      fullWidth
      disabled={disabled}
      value={localValue}
      inputRef={inputRef}
      InputProps={{ style: { color: "#fff", fontSize: "12px" } }}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={onCommit}
      onChange={(event) => {
        const raw = event.target.value;
        // A literally typed "\n" is what someone means by a line break; keep the
        // caret where they left it so typing does not jump.
        if (raw.includes("\\n")) {
          const cursor = event.target.selectionStart;
          const replaced = raw.replace(/\\n/g, "\n");
          const newCursor = raw.slice(0, cursor).replace(/\\n/g, "\n").length;
          setLocalValue(replaced);
          onChange(replaced);
          requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
          });
        } else {
          setLocalValue(raw);
          onChange(raw);
        }
      }}
    />
  );
}

/**
 * The pointer-lock scrub helpers, matched to the Scene Editor inspector.
 *
 * Pointer lock is what makes a drag unbounded: `movementX` keeps reporting after
 * the physical cursor has stopped at the edge of the monitor, so a value can be
 * dragged as far as it needs to go. The browser hides the real cursor while the
 * lock is held, so a stand-in is drawn — the same ew-resize glyph, wrapping at
 * the window edges so it never disappears.
 */
function setPreviewIframePointerEvents(enabled) {
  const iframe = document.getElementById(previewIframeId);
  if (!iframe) return;
  iframe.style.pointerEvents = enabled ? "" : "none";
}

function wrapCoord(value, size) {
  if (!Number.isFinite(size) || size <= 0) return value;
  const mod = value % size;
  return mod < 0 ? mod + size : mod;
}

/** System-like ew-resize stand-in (pointer lock hides the real OS cursor). */
function ensureScrubCursor() {
  let element = document.getElementById(SCRUB_CURSOR_ID);
  if (element) return element;
  element = document.createElement("div");
  element.id = SCRUB_CURSOR_ID;
  element.setAttribute("aria-hidden", "true");
  Object.assign(element.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: "24px",
    height: "24px",
    marginLeft: "-12px",
    marginTop: "-12px",
    pointerEvents: "none",
    zIndex: "2147483647",
    willChange: "transform",
    cursor: "ew-resize",
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
        '<path d="M7 12H17M9 9L6 12L9 15M15 9L18 12L15 15" fill="none" stroke="black" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M7 12H17M9 9L6 12L9 15M15 9L18 12L15 15" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>"
    )}")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "24px 24px",
  });
  document.body.appendChild(element);
  return element;
}

function moveScrubCursor(x, y) {
  ensureScrubCursor().style.transform = `translate3d(${x}px, ${y}px, 0)`;
}

function hideScrubCursor() {
  const element = document.getElementById(SCRUB_CURSOR_ID);
  if (element) element.remove();
}

/**
 * Number entry with drag-to-scrub, arrow buttons, and keyboard stepping.
 *
 * The Scene Editor's number field, with its gesture machinery intact: a
 * horizontal drag scrubs under pointer lock so it never runs out of screen,
 * the arrows step and auto-repeat while held, and Up/Down do the same from the
 * keyboard. Shift takes the coarse step — ten times the declared one, or the
 * panel default when the design declares none.
 *
 * Typing is unconstrained until the string is a complete finite number, so
 * intermediate states ("", "-", "1.") never reach the snapshot.
 *
 * What the Scene Editor's version carries and this one does not is the
 * multi-select `mixed` state and the `{gesture}` options it passes on: the
 * Design tab edits one snapshot, so a field has exactly one value and a write
 * is a write.
 */
export function NumberField({
  value,
  disabled,
  onChange,
  onCommit = () => {},
  min = null,
  max = null,
  step = null,
  label = null,
  integer = false,
}) {
  const [focused, setFocused] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const inputRef = useRef(null);
  // What the gesture is stepping from. The display string is not enough: a
  // repeat fires every 60 ms and has to accumulate from the last value it wrote,
  // not from whatever React has painted.
  const currentNumRef = useRef(Number(value) || 0);
  const shiftHeldRef = useRef(false);
  const arrowRepeatRef = useRef(null);
  // Typing and gesturing both own the field until they end; the committed value
  // catching up is not a reason to redraw what is being edited.
  const {
    value: localValue,
    ref: localRef,
    set: setLocalValue,
    release: releaseValue,
  } = useEditableValue(String(value ?? ""), focused || gesturing);

  const inputCursor = disabled ? "not-allowed" : focused ? "text" : "ew-resize";
  const hasStep = Number.isFinite(step) && step > 0;
  // A whole-number field steps in whole numbers: an `integer` row scrubbing by
  // 0.01 would write the decimals the type exists to keep out.
  const arrowStep = hasStep ? step : integer ? 1 : ARROW_STEP;
  const arrowStepShift = hasStep ? step * 10 : integer ? 10 : ARROW_STEP_SHIFT;
  const scrubStep = hasStep ? step : integer ? 1 : SCRUB_STEP;
  const scrubStepShift = hasStep ? step * 10 : integer ? 10 : SCRUB_STEP_SHIFT;

  function clamp(num) {
    let next = integer ? Math.round(num) : num;
    if (min !== null && Number.isFinite(min) && next < min) next = min;
    if (max !== null && Number.isFinite(max) && next > max) next = max;
    return next;
  }

  function setInputCursor(cursor) {
    if (inputRef.current) inputRef.current.style.cursor = cursor;
  }

  // The document's value is adopted between gestures only — mid-drag it is the
  // echo of what this field just wrote, and taking it back would fight the drag.
  useEffect(() => {
    if (gesturing) return;
    const num = Number(value);
    currentNumRef.current = isFinite(num) ? num : 0;
  }, [value, gesturing]);

  useEffect(() => {
    setInputCursor(inputCursor);
  }, [inputCursor]);

  function clearArrowRepeat() {
    const active = arrowRepeatRef.current;
    if (!active) return;
    if (active.timeout) clearTimeout(active.timeout);
    if (active.interval) clearInterval(active.interval);
    if (active.cleanupListeners) active.cleanupListeners();
    arrowRepeatRef.current = null;
  }

  useEffect(() => () => clearArrowRepeat(), []);

  function scrubBase() {
    const trimmed = String(localRef.current ?? "").trim();
    if (trimmed === "" || trimmed === "-" || trimmed.endsWith(".")) {
      const fallback = Number(value);
      return isFinite(fallback) ? fallback : 0;
    }
    const num = Number(trimmed);
    return isFinite(num) ? num : 0;
  }

  /** One step of a gesture: round to the step in play, show it, publish it. */
  function commitGestureValue(num, stepSize) {
    const rounded = clamp(roundToStep(roundFloatNoise(num), stepSize));
    const unchanged = rounded === currentNumRef.current;
    currentNumRef.current = rounded;
    setLocalValue(String(rounded));
    if (unchanged) return;
    onChange(rounded);
  }

  /** The field goes back to the document, and the pending write is flushed. */
  function endGesture() {
    setGesturing(false);
    releaseValue();
    onCommit();
  }

  function stepByArrow(direction, shiftKey) {
    const stepSize = shiftKey ? arrowStepShift : arrowStep;
    commitGestureValue(currentNumRef.current + direction * stepSize, stepSize);
  }

  function handleArrowPointerDown(direction, event) {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    clearArrowRepeat();
    setGesturing(true);
    shiftHeldRef.current = event.shiftKey;
    stepByArrow(direction, shiftHeldRef.current);

    const trackShift = (shiftEvent) => {
      shiftHeldRef.current = shiftEvent.shiftKey;
    };
    document.addEventListener("keydown", trackShift);
    document.addEventListener("keyup", trackShift);

    const cleanupListeners = () => {
      document.removeEventListener("keydown", trackShift);
      document.removeEventListener("keyup", trackShift);
      document.removeEventListener("mouseup", onPointerUp);
    };

    function onPointerUp() {
      clearArrowRepeat();
      endGesture();
    }

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        stepByArrow(direction, shiftHeldRef.current);
      }, ARROW_REPEAT_INTERVAL_MS);
      if (arrowRepeatRef.current) arrowRepeatRef.current.interval = interval;
    }, ARROW_REPEAT_DELAY_MS);

    document.addEventListener("mouseup", onPointerUp);
    arrowRepeatRef.current = { timeout, interval: null, cleanupListeners };
  }

  function handleScrubMouseDown(event) {
    if (disabled || event.button !== 0) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const startValue = scrubBase();
    let scrubbing = false;
    let ended = false;
    let lockEngaged = false;
    // Accumulated horizontal movement — keeps growing past the window edge once
    // pointer lock is held.
    let accumX = 0;
    let cursorX = startX;
    let cursorY = startY;
    let scrubRaf = 0;
    let pendingShiftKey = false;

    function applyAccum(shiftKey) {
      const stepSize = shiftKey ? scrubStepShift : scrubStep;
      commitGestureValue(startValue + (accumX / STEP_PIXEL) * stepSize, stepSize);
    }

    // One write per frame however fast the mouse reports: the panel forwards
    // every change to the preview and the store, and a 1000 Hz mouse would
    // otherwise queue five of those per painted frame.
    function scheduleScrubApply(shiftKey) {
      pendingShiftKey = shiftKey;
      if (scrubRaf) return;
      scrubRaf = requestAnimationFrame(() => {
        scrubRaf = 0;
        applyAccum(pendingShiftKey);
      });
    }

    function updateWrappedCursor(movementX, movementY) {
      cursorX = wrapCoord(cursorX + (movementX || 0), window.innerWidth);
      cursorY = wrapCoord(cursorY + (movementY || 0), window.innerHeight);
      moveScrubCursor(cursorX, cursorY);
    }

    function beginScrub(initialDeltaX) {
      scrubbing = true;
      setGesturing(true);
      setPreviewIframePointerEvents(false);
      document.body.style.cursor = "none";
      document.body.style.userSelect = "none";
      setInputCursor("none");
      if (inputRef.current) inputRef.current.blur();
      accumX = initialDeltaX;
      cursorX = wrapCoord(startX + initialDeltaX, window.innerWidth);
      cursorY = startY;
      moveScrubCursor(cursorX, cursorY);

      const lockTarget = document.documentElement;
      try {
        const maybePromise = lockTarget.requestPointerLock({ unadjustedMovement: true });
        if (maybePromise && typeof maybePromise.catch === "function") {
          maybePromise.catch(() => {
            try {
              lockTarget.requestPointerLock();
            } catch (_err) {
              // lock refused — fall back to clientX tracking below
            }
          });
        }
      } catch (_err) {
        try {
          lockTarget.requestPointerLock();
        } catch (__err) {
          // lock refused — fall back to clientX tracking below
        }
      }

      scheduleScrubApply(false);
    }

    function onMouseMove(moveEvent) {
      if (!scrubbing) {
        const provisional = moveEvent.clientX - startX;
        // A few pixels of slop so a click that lands in the field still focuses it.
        if (Math.abs(provisional) < 3) return;
        beginScrub(provisional);
        return;
      }

      moveEvent.preventDefault();
      if (lockEngaged || document.pointerLockElement) {
        lockEngaged = true;
        accumX += moveEvent.movementX || 0;
        updateWrappedCursor(moveEvent.movementX, moveEvent.movementY);
      } else {
        // Lock denied or not yet granted: track clientX, edge-limited.
        accumX = moveEvent.clientX - startX;
        cursorX = moveEvent.clientX;
        cursorY = moveEvent.clientY;
        moveScrubCursor(cursorX, cursorY);
      }
      scheduleScrubApply(moveEvent.shiftKey);
    }

    function onMouseUp() {
      if (ended) return;
      ended = true;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      window.removeEventListener("blur", onMouseUp);
      if (scrubRaf) {
        cancelAnimationFrame(scrubRaf);
        scrubRaf = 0;
        applyAccum(pendingShiftKey);
      }
      if (document.pointerLockElement) {
        try {
          document.exitPointerLock();
        } catch (_err) {
          // already released
        }
      }
      hideScrubCursor();
      setPreviewIframePointerEvents(true);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      const isFocused = document.activeElement === inputRef.current;
      setInputCursor(disabled ? "not-allowed" : isFocused ? "text" : "ew-resize");
      if (scrubbing) endGesture();
    }

    function onPointerLockChange() {
      if (document.pointerLockElement) {
        lockEngaged = true;
        return;
      }
      // Esc (or browser UI) exits the lock mid-scrub — finish the gesture cleanly.
      if (scrubbing && !ended) onMouseUp();
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("pointerlockchange", onPointerLockChange);
    window.addEventListener("blur", onMouseUp);
  }

  return (
    <TextField
      type="text"
      inputMode="decimal"
      size="small"
      fullWidth
      disabled={disabled}
      value={localValue}
      inputRef={inputRef}
      InputProps={{
        style: { color: "#fff", fontSize: "12px" },
        // The axis of a vec2 rides inside the field rather than beside it, so
        // two of them fill the row evenly however narrow the panel gets.
        startAdornment: label ? (
          <InputAdornment position="start" style={{ margin: "0 0 0 4px" }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#8a8d93",
                textTransform: "lowercase",
              }}
            >
              {label}
            </span>
          </InputAdornment>
        ) : undefined,
        endAdornment: !disabled ? (
          <InputAdornment
            position="end"
            style={{ alignSelf: "center", marginRight: 6, height: "auto" }}
          >
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 0 }}>
              <IconButton
                size="small"
                tabIndex={-1}
                title={`Increment (+${arrowStep}, Shift +${arrowStepShift})`}
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(event) => handleArrowPointerDown(1, event)}
              >
                <KeyboardArrowUpIcon style={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                tabIndex={-1}
                title={`Decrement (−${arrowStep}, Shift −${arrowStepShift})`}
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(event) => handleArrowPointerDown(-1, event)}
              >
                <KeyboardArrowDownIcon style={{ fontSize: 13 }} />
              </IconButton>
            </div>
          </InputAdornment>
        ) : undefined,
      }}
      inputProps={{
        readOnly: !focused,
        onMouseDown: handleScrubMouseDown,
        style: { cursor: inputCursor },
      }}
      style={{ width: "100%" }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (disabled) return;
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          stepByArrow(event.key === "ArrowUp" ? 1 : -1, event.shiftKey);
          onCommit();
        }
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const trimmed = String(localValue).trim();
        const complete =
          trimmed !== "" && trimmed !== "-" && !trimmed.endsWith(".") && isFinite(Number(trimmed));
        // Half-typed text never reached the pending value; stop showing it.
        if (!complete) setLocalValue(String(value ?? ""));
        // The field is the document's again: leaving it is where a committed
        // value is allowed to reformat what is shown (0.50 → 0.5).
        releaseValue();
        onCommit();
      }}
      onChange={(event) => {
        const raw = event.target.value;
        setLocalValue(raw);

        const trimmed = raw.trim();
        if (trimmed === "" || trimmed === "-" || trimmed.endsWith(".")) return;
        const num = Number(trimmed);
        if (!isFinite(num)) return;
        const clamped = clamp(num);
        currentNumRef.current = clamped;
        if (clamped !== num) setLocalValue(String(clamped));
        onChange(clamped);
      }}
    />
  );
}

/**
 * A bounded number, as a track with a readout.
 *
 * Only reachable when the design declares both `min` and `max` — a slider
 * without bounds is a thumb with nowhere to go, which is why normalizeDesign
 * refuses to produce one.
 */
export function SliderField({ value, disabled, onChange, onCommit = () => {}, min, max, step }) {
  const resolvedValue = isFinite(Number(value)) ? Number(value) : min;
  const [dragging, setDragging] = useState(false);
  const resolvedStep = step || 0.01;
  const { value: current, set: setCurrent, release: releaseValue } = useEditableValue(
    resolvedValue,
    dragging
  );

  function endDrag() {
    setDragging(false);
    releaseValue();
    onCommit();
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
      <input
        type="range"
        disabled={disabled}
        min={min}
        max={max}
        step={resolvedStep}
        value={current}
        onMouseDown={() => setDragging(true)}
        onTouchStart={() => setDragging(true)}
        onChange={(event) => {
          const next = roundFloatNoise(Number(event.target.value));
          setCurrent(next);
          onChange(next);
        }}
        onMouseUp={endDrag}
        onTouchEnd={endDrag}
        onKeyUp={endDrag}
        onBlur={endDrag}
        style={{
          flex: "1 1 0",
          minWidth: 0,
          accentColor: "#64b5f6",
          cursor: disabled ? "default" : "pointer",
        }}
      />
      {/* The readout is the panel's own number field, not a label: everything
          numeric in this product is scrubbable and steppable, and a slider is
          the one place where a value can be read but — until now — not typed. */}
      <div style={{ flexShrink: 0, width: 88 }}>
        <NumberField
          value={roundFloatNoise(current)}
          disabled={disabled}
          min={min}
          max={max}
          step={resolvedStep}
          onChange={(next) => {
            setCurrent(next);
            onChange(next);
          }}
          onCommit={() => {
            releaseValue();
            onCommit();
          }}
        />
      </div>
    </div>
  );
}

function expandShortHex(hex) {
  const value = String(hex ?? "");
  if (/^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(value)) return value.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    const short = value.slice(1);
    return `#${short[0]}${short[0]}${short[1]}${short[1]}${short[2]}${short[2]}`.toLowerCase();
  }
  return "#000000";
}

function normalizeHex(value) {
  const hex = String(value ?? "#000000");
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)
    ? expandShortHex(hex)
    : "#000000";
}

/** Colour swatch plus the hex, so a value can be picked or pasted. */
export function ColorField({ value, disabled, onChange }) {
  const [localValue, setLocalValue] = useState(String(value ?? "#000000"));
  const latestRef = useRef(localValue);
  const committedRef = useRef(normalizeHex(value));

  useEffect(() => {
    const next = String(value ?? "#000000");
    setLocalValue(next);
    latestRef.current = next;
    committedRef.current = normalizeHex(next);
  }, [value]);

  function queueColor(hex) {
    const next = normalizeHex(hex);
    latestRef.current = next;
    committedRef.current = next;
    setLocalValue(next);
    if (!disabled) onChange(next);
  }

  function handleHexChange(event) {
    const next = event.target.value;
    if (!/^#[0-9a-f]{0,8}$/i.test(next)) return;
    latestRef.current = next;
    setLocalValue(next);

    // Keep drafts local. Six and eight digits are unambiguous complete values;
    // a short three-digit color is accepted when the field loses focus.
    if (/^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(next)) queueColor(next);
  }

  function handleHexBlur() {
    const latest = String(latestRef.current || "");
    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(latest)) {
      queueColor(latest);
      return;
    }

    // Do not let a half-typed value replace the last usable color.
    latestRef.current = committedRef.current;
    setLocalValue(committedRef.current);
  }

  // Native color inputs only accept #rrggbb. Preserve an authored alpha suffix
  // in the text field while showing its RGB part in the swatch.
  const swatchValue = (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(localValue)
    ? normalizeHex(localValue)
    : committedRef.current
  ).slice(0, 7);

  return (
    <TextField
      size="small"
      fullWidth
      value={localValue}
      disabled={disabled}
      onKeyDown={(event) => event.stopPropagation()}
      onChange={handleHexChange}
      onBlur={handleHexBlur}
      InputProps={{
        style: {
          color: "#aaa",
          fontSize: "12px",
          fontFamily: "monospace",
          height: 24,
        },
        startAdornment: (
          <InputAdornment position="start" style={{ marginLeft: 4, marginRight: 0, height: "auto" }}>
            <input
              type="color"
              className="design-color-swatch"
              value={swatchValue}
              disabled={disabled}
              title="Pick color"
              style={{
                width: 18,
                height: 18,
                boxSizing: "border-box",
                border: "1px solid #3a3a3f",
                borderRadius: 3,
                padding: 0,
                background: "transparent",
                cursor: disabled ? "default" : "pointer",
                display: "block",
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => queueColor(event.target.value)}
            />
          </InputAdornment>
        ),
      }}
    />
  );
}

/** An {x, y} pair on one line. */
export function Vec2Field({
  value,
  disabled,
  onChange,
  onCommit = () => {},
  min = null,
  max = null,
  step = null,
}) {
  const incoming = value && typeof value === "object" ? value : { x: 0, y: 0 };
  const { value: point, ref: pointRef, set: setPoint, release: releasePoint } =
    useEditableValue(incoming);

  function changeAxis(axis, nextValue) {
    const nextPoint = { ...pointRef.current, [axis]: nextValue };
    pointRef.current = nextPoint;
    setPoint(nextPoint);
    onChange(nextPoint);
  }

  // The axis rides inside its own field as an adornment — the Scene Editor's
  // arrangement, and the reason two number fields fit a narrow row without the
  // labels stealing width from the values.
  return (
    <div style={{ display: "flex", gap: 6, width: "100%" }}>
      {["x", "y"].map((axis) => (
        <div key={axis} style={{ flex: 1, minWidth: 0 }}>
          <NumberField
            value={point[axis]}
            disabled={disabled}
            label={axis}
            min={min}
            max={max}
            step={step}
            onChange={(next) => changeAxis(axis, next)}
            onCommit={() => {
              releasePoint();
              onCommit();
            }}
          />
        </div>
      ))}
    </div>
  );
}

export function DropdownField({ value, disabled, onChange, onCommit = () => {}, options }) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // A value the design does not list still has to be visible — otherwise the
  // control silently shows the wrong thing and the next edit writes it back.
  const known = options.some((option) => option.value === localValue);

  return (
    <Select
      size="small"
      fullWidth
      disabled={disabled}
      value={known ? localValue : ""}
      displayEmpty
      onChange={(event) => {
        const next = event.target.value;
        setLocalValue(next);
        onChange(next);
        onCommit();
      }}
      style={{ fontSize: "12px", color: "#fff" }}
      // A menu opens in a portal, outside the panel and outside its CSS, so the
      // surface has to be named here or it lands on MUI's default light paper.
      MenuProps={{ PaperProps: { style: { backgroundColor: CARD_BG, maxHeight: 300 } } }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {!known && (
        <MenuItem value="" disabled style={{ color: "#eee", fontSize: "12px" }}>
          {localValue === undefined ? "—" : `${JSON.stringify(localValue)} (not in design)`}
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem
          key={String(option.value)}
          value={option.value}
          style={{ color: "#eee", fontSize: "12px" }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Arrays and loose objects, as JSON.
 *
 * The design vocabulary has no control for "a list of eighteen numbers", and
 * inventing one would be guessing at what the list means. A textarea that only
 * commits valid JSON is honest about that, and keeps the value reachable.
 */
export function JsonField({ value, disabled, onChange, onCommit = () => {}, fieldKey, label }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        fullWidth
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={() => setOpen(true)}
        startIcon={<EditIcon style={{ fontSize: "14px" }} />}
        style={{
          ...ROW_BUTTON_STYLE,
          marginLeft: 0,
          color: FIELD_LABEL,
          borderColor: SECTION_DIVIDER,
          justifyContent: "center",
        }}
      >
        Edit
      </Button>
      {open && (
        <DialogTextEdit
          open
          variant="dark"
          rowsMax={40}
          width="46rem"
          label={label || fieldKey}
          storageKey={fieldKey}
          value={value}
          setValue={(next) => {
            onChange(next);
            onCommit();
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** The dark input chrome. Scoped to the panel so nothing else inherits it. */
/** A small outlined chip, the size the asset panels use on a crowded row. */
const ROW_BUTTON_STYLE = {
  fontSize: 10,
  textTransform: "none",
  padding: "2px 6px",
  minWidth: 0,
  marginLeft: 4,
  whiteSpace: "nowrap",
};

/** What an asset is, for the types with nothing to preview. */
export function AssetTypeBadge({ type }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 60,
        height: 30,
        marginRight: 8,
        borderRadius: 3,
        border: "1px solid #3a3a3f",
        backgroundColor: "#212226",
        fontSize: 9,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        color: "#8a8d93",
      }}
    >
      {type || "asset"}
    </span>
  );
}

/**
 * One asset slot: what is in it, and how to put something else there.
 *
 * The row is the version-settings dependency slot rearranged for a design: the
 * same 30x60 thumbnail, the same small outlined chips, and the same blue
 * underlined key that jumps to the asset in the Assets tab. What it does NOT
 * carry is that panel's binding machinery — a design row names one asset key
 * and only ever replaces what is in it, so there is no dropdown of alternatives
 * and no component to re-point.
 *
 * IT DECIDES NOTHING. Which affordances exist is the host's answer, handed down
 * as callbacks: no `onUpload`/`onPickFromLibrary` means this editor cannot
 * supply bytes (the version editor, where the media library lives in the Assets
 * tab), and the row offers the jump instead.
 *
 * Every callback takes the asset key back as its first argument, so the panel
 * can hold ONE stable function per action instead of minting a closure per row —
 * the rows are memoised, and a fresh callback identity would defeat that.
 */
export function AssetField({
  assetKey,
  entry,
  thumbnail = null,
  accept = "*",
  disabled = false,
  busy = false,
  onUpload = null,
  onPickFromLibrary = null,
  onNavigate = null,
}) {
  const inputId = `design-asset-file-${assetKey}`;
  const type = entry && entry.type ? entry.type : "asset";
  const canReplaceHere = Boolean(onUpload || onPickFromLibrary);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, width: "100%", minWidth: 0 }}>
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          style={{
            height: 30,
            maxWidth: 60,
            objectFit: "contain",
            marginRight: 8,
            flexShrink: 0,
            borderRadius: 3,
          }}
          onError={(event) => {
            event.target.style.display = "none";
          }}
        />
      ) : (
        <AssetTypeBadge type={type} />
      )}

      <div style={{ flex: "1 1 0", minWidth: 0 }}>
        {onNavigate ? (
          <span
            role="link"
            tabIndex={0}
            title="Open this asset in the Assets tab"
            onClick={() => onNavigate(assetKey)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onNavigate(assetKey);
            }}
            style={{
              display: "block",
              fontSize: 12,
              fontFamily: "monospace",
              color: "#7e9bd6",
              textDecoration: "underline",
              textDecorationColor: "rgba(126, 155, 214, 0.5)",
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {assetKey}
          </span>
        ) : (
          <span
            title={assetKey}
            style={{
              display: "block",
              fontSize: 12,
              fontFamily: "monospace",
              color: "#9ad6c4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {assetKey}
          </span>
        )}
        <span style={{ fontSize: 10, color: "#7f838a" }}>
          {type} · {describeAssetSize(entry)}
        </span>
      </div>

      {busy ? (
        <CircularProgress size={16} style={{ marginLeft: 6, flexShrink: 0 }} />
      ) : canReplaceHere ? (
        <>
          {onUpload && (
            <>
              {/* Opened from the click handler, never at render: this module is
                  covered by a suite with no DOM. */}
              <input
                id={inputId}
                type="file"
                hidden
                accept={accept}
                onChange={(event) => {
                  const file = event.target.files && event.target.files[0];
                  event.target.value = "";
                  if (file) onUpload(assetKey, file);
                }}
              />
              <Tooltip title="Replace with a file from your computer" arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    startIcon={<PublishIcon style={{ fontSize: 13 }} />}
                    style={ROW_BUTTON_STYLE}
                    onClick={() => {
                      const input = document.getElementById(inputId);
                      if (input) input.click();
                    }}
                  >
                    Upload
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {onPickFromLibrary && (
            <Tooltip title="Replace from the asset library" arrow>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={disabled}
                  startIcon={<PhotoLibraryIcon style={{ fontSize: 13 }} />}
                  style={ROW_BUTTON_STYLE}
                  onClick={() => onPickFromLibrary(assetKey)}
                >
                  Library
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      ) : (
        <Tooltip
          title={
            onNavigate
              ? "This editor keeps its asset library in the Assets tab"
              : "Replace this asset from the Assets tab"
          }
          arrow
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!onNavigate}
              startIcon={<OpenInNewIcon style={{ fontSize: 13 }} />}
              style={ROW_BUTTON_STYLE}
              onClick={() => onNavigate && onNavigate(assetKey)}
            >
              Replace in Assets
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export const DESIGN_PANEL_CSS = `
  .design-props-panel .MuiInputBase-root {
    background-color: #212226;
    border: 1px solid #3a3a3f;
    border-radius: 4px;
    min-height: 24px;
  }
  .design-props-panel .MuiInputBase-root.Mui-focused { border-color: #64b5f6; }
  .design-props-panel .MuiInputBase-root.Mui-disabled {
    background-color: #1c1d20;
    border-color: #2f3033;
  }
  .design-props-panel .MuiInputBase-input { padding: 3px 6px; font-size: 12px; }
  .design-props-panel .MuiSelect-icon { color: #8a8d93; }
  .design-props-panel .MuiInput-underline:before,
  .design-props-panel .MuiInput-underline:after { display: none; }
  .design-props-panel .MuiSelect-select { padding: 3px 24px 3px 6px; background: transparent; }
  .design-props-panel .MuiSwitch-root { margin: 0; }
  .design-props-panel .design-color-swatch {
    -webkit-appearance: none;
    appearance: none;
  }
  .design-props-panel .design-color-swatch::-webkit-color-swatch-wrapper { padding: 0; }
  .design-props-panel .design-color-swatch::-webkit-color-swatch { border: none; border-radius: 3px; }
  .design-props-panel .design-color-swatch::-moz-color-swatch { border: none; border-radius: 3px; }
`;
